import React, { useEffect, useMemo, useState } from 'react';
import { ArrowLeft, BookOpen, HeartHandshake, Loader2, UsersRound } from 'lucide-react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { bookstoreApi } from '@/lib/bookstoreApi';
import BookCard from '@/components/bookstore/BookCard';
import BookDetailModal from '@/components/bookstore/BookDetailModal';
import SpinCarousel from '@/components/bookstore/SpinCarousel';
import ProductionNowCard from '@/components/bookstore/ProductionNowCard';
import { FONTS, GRAIN_URL, P, useScriptoriumFonts } from '@/components/landing/scriptorium/palette';
import { useSeoMeta } from '@/lib/useSeoMeta';

const GROUP_COLORS = {
  lgbtqia: ['#7a3f8e', '#c95d6a'],
  'black-minority-offerings': ['#194f46', '#bd8b3c'],
  'regional-non-fiction': ['#38556b', '#8c6848'],
};

const CategoryTile = ({ item, active, onClick, groupSlug }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={`community-category-${groupSlug}-${item.genre.slug}`}
    className={`flex aspect-[4/3] w-40 shrink-0 flex-col justify-between border p-4 text-left transition-transform duration-200 hover:-translate-y-1 sm:w-48 ${active ? 'shadow-lg' : ''}`}
    style={{
      borderColor: active ? P.oxblood : `${P.gold}77`,
      background: active ? `${P.oxblood}0d` : P.paperDeep,
      color: P.ink,
    }}
  >
    <BookOpen className="h-5 w-5" style={{ color: active ? P.oxblood : P.leather }} />
    <div>
      <div className="font-semibold" style={{ fontFamily: FONTS.display }}>{item.genre.name}</div>
      <div className="mt-1 text-[10px] uppercase tracking-[0.16em]" style={{ color: P.inkSoft }}>{item.book_count} titles</div>
    </div>
  </button>
);

export default function CommunityDiverseLanding() {
  useScriptoriumFonts();
  useSeoMeta('/bookstore/g/our-community-is-diverse', {
    title: 'Our Community Is Diverse · Crown & Rose Bookshelf',
    description: 'Browse LGBTQIA+, Black and Minority Offerings, and Regional Non-Fiction across romance, science fiction, mystery, fantasy, literary fiction, and more.',
  });
  const [data, setData] = useState(null);
  const [activeGroup, setActiveGroup] = useState('lgbtqia');
  const [activeCategory, setActiveCategory] = useState('fiction');
  const [openBook, setOpenBook] = useState(null);

  useEffect(() => {
    bookstoreApi.communityDiversity()
      .then((payload) => {
        setData(payload);
        const first = payload.groups?.find((row) => row.book_count > 0) || payload.groups?.[0];
        if (first) {
          setActiveGroup(first.group.slug);
          setActiveCategory(first.categories.find((row) => row.book_count > 0)?.genre?.slug || first.categories[0]?.genre?.slug || 'fiction');
        }
      })
      .catch(() => toast.error('The community shelves could not be loaded.'));
  }, []);

  const group = useMemo(
    () => data?.groups?.find((row) => row.group.slug === activeGroup),
    [data, activeGroup],
  );
  const category = group?.categories?.find((row) => row.genre.slug === activeCategory);

  const chooseGroup = (row) => {
    bookstoreApi.trackStorefrontEvent({ event_type: 'community_subgenre_click', community_slug: row.group.slug, genre_slug: row.group.slug });
    setActiveGroup(row.group.slug);
    setActiveCategory(row.categories.find((item) => item.book_count > 0)?.genre?.slug || row.categories[0]?.genre?.slug || 'fiction');
  };

  return (
    <main className="relative min-h-screen overflow-hidden" style={{ backgroundColor: P.paper, color: P.ink, fontFamily: FONTS.body }} data-testid="community-diverse-bookshelf-page">
      <div className="pointer-events-none fixed inset-0 opacity-25" style={{ backgroundImage: GRAIN_URL }} />
      <header className="sticky top-0 z-30 border-b" style={{ borderColor: `${P.gold}66`, backgroundColor: `${P.paper}ee`, backdropFilter: 'blur(8px)' }}>
        <div className="mx-auto flex max-w-6xl items-center justify-between px-5 py-3">
          <Link to="/bookstore" className="inline-flex items-center gap-1 text-xs font-semibold" style={{ color: P.inkSoft }} data-testid="community-diverse-back-link"><ArrowLeft className="h-3.5 w-3.5" /> All shelves</Link>
          <div className="inline-flex items-center gap-2 border px-3 py-1 text-[10px] uppercase tracking-[0.2em]" style={{ borderColor: `${P.gold}88`, color: P.oxblood }}><HeartHandshake className="h-3.5 w-3.5" />Community discovery</div>
        </div>
      </header>

      {!data ? (
        <div className="relative flex min-h-[70vh] items-center justify-center" data-testid="community-diverse-loading"><Loader2 className="mr-2 h-5 w-5 animate-spin" />Loading community shelves…</div>
      ) : (
        <div className="relative mx-auto max-w-6xl px-5 py-12 sm:px-8">
          <div className="max-w-4xl">
            <div className="text-[10px] uppercase tracking-[0.28em]" style={{ color: P.oxblood, fontFamily: FONTS.type }}>The Vault · community bookshelf</div>
            <h1 className="mt-3 text-4xl font-semibold sm:text-5xl lg:text-6xl" style={{ fontFamily: FONTS.display }} data-testid="community-diverse-heading">{data.parent.name}</h1>
            <p className="mt-5 max-w-3xl text-sm leading-7 sm:text-base" style={{ color: P.inkSoft }}>{data.parent.manifesto || data.parent.blurb}</p>
          </div>

          <div className="mt-10 grid gap-3 md:grid-cols-3" data-testid="community-subgenre-grid">
            {data.groups.map((row) => {
              const active = row.group.slug === activeGroup;
              const colors = GROUP_COLORS[row.group.slug] || [P.leather, P.goldDeep];
              return <button key={row.group.slug} type="button" onClick={() => chooseGroup(row)} data-testid={`community-subgenre-${row.group.slug}`} className="min-h-40 border p-5 text-left transition-transform duration-200 hover:-translate-y-1" style={{ borderColor: active ? P.oxblood : `${P.gold}66`, background: `linear-gradient(135deg, ${colors[0]}18, ${colors[1]}22)` }}><UsersRound className="h-5 w-5" style={{ color: colors[0] }} /><div className="mt-7 text-lg font-semibold" style={{ fontFamily: FONTS.display }}>{row.group.name}</div><p className="mt-1 text-xs leading-5" style={{ color: P.inkSoft }}>{row.group.blurb}</p><div className="mt-3 text-[10px] uppercase tracking-[0.16em]" style={{ color: P.oxblood }}>{row.book_count} curated titles</div></button>;
            })}
          </div>

          {group && <section className="mt-14 border-t pt-8" style={{ borderColor: `${P.gold}66` }} data-testid={`community-subgenre-section-${group.group.slug}`}>
            <div className="max-w-3xl"><div className="text-[10px] uppercase tracking-[0.24em]" style={{ color: P.oxblood }}>Browse within the shelf</div><h2 className="mt-2 text-base font-semibold md:text-lg" style={{ fontFamily: FONTS.display }}>{group.group.name}</h2><p className="mt-2 text-sm" style={{ color: P.inkSoft }}>{group.group.blurb}</p></div>
            <div className="mt-4">
              <SpinCarousel testid={`community-genre-carousel-${group.group.slug}`} ariaLabel={`${group.group.name} genres`}>
                {group.categories.map((item) => <CategoryTile key={item.genre.slug} item={item} groupSlug={group.group.slug} active={item.genre.slug === activeCategory} onClick={() => { bookstoreApi.trackStorefrontEvent({ event_type: 'community_category_click', community_slug: group.group.slug, genre_slug: item.genre.slug }); setActiveCategory(item.genre.slug); }} />)}
              </SpinCarousel>
            </div>

            {category && <div className="border-t pt-7" style={{ borderColor: `${P.gold}55` }} data-testid={`community-book-rail-${group.group.slug}-${category.genre.slug}`}>
              <div className="flex items-end justify-between gap-4"><div><div className="text-[10px] uppercase tracking-[0.2em]" style={{ color: P.inkSoft }}>Selected genre</div><h3 className="mt-1 text-lg font-semibold" style={{ fontFamily: FONTS.display }}>{category.genre.name}</h3></div><div className="text-xs" style={{ color: P.inkSoft }}>{category.book_count} titles</div></div>
              <SpinCarousel testid={`community-books-${group.group.slug}-${category.genre.slug}`}>
                {category.books.length ? category.books.map((book) => <BookCard key={book.id} book={book} onClick={setOpenBook} />) : <ProductionNowCard shelf={`${group.group.name} · ${category.genre.name}`} testId={`books-in-production-${group.group.slug}-${category.genre.slug}`} />}
              </SpinCarousel>
            </div>}
          </section>}
        </div>
      )}
      {openBook && <BookDetailModal book={openBook} onClose={() => setOpenBook(null)} />}
    </main>
  );
}